import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';

import { ApiConnectService } from './api-connect.service';
import { ContentTranslation } from '../models/content-translation.model';
import { UserService } from './user.service';
import { User } from '../models/user.model';
import { Subscription } from 'rxjs/Subscription';

export type UiLocale = 'ru' | 'en' | 'ch' | 'bs';
export type ContentLocale = 'ru' | 'en' | 'zh';

const CHINESE_UI_LOCALE: UiLocale = 'ch';
const CHINESE_CONTENT_LOCALE: ContentLocale = 'zh';
const RAW_CONTENT_UI_LOCALE: UiLocale = 'bs';
const RAW_CONTENT_DOCUMENT_LANGUAGE = 'ru';
const EMPTY_CONTENT = '';
const BROWSER_STORAGE = localStorage;
const PAGE_DOCUMENT = document;
const AUTHENTICATED_STORAGE_KEY = 'Authenticated';
const AUTHENTICATED_VALUE = 'true';
const UI_LOCALE_STORAGE_KEY = 'lang';

@Injectable()
export class UiLocaleService implements OnDestroy {
  private static readonly DEFAULT_LOCALE: UiLocale = 'en';
  private readonly persistence = new Subscription();

  constructor(private http: HttpClient,
              private translate: TranslateService,
              private api: ApiConnectService,
              private userService: UserService) {
  }

  initialize(): UiLocale {
    const user = this.getLocalUser();
    return this.applyServerLocale(user && user.uiLocale, user && user.language);
  }

  applyServerLocale(serverLocale?: string | null, legacyLanguage?: string | null): UiLocale {
    const locale = this.fromServerLocale(serverLocale)
      || this.normalizeUiLocale(localStorage.getItem('lang'))
      || this.fromLegacyLanguage(legacyLanguage)
      || UiLocaleService.DEFAULT_LOCALE;
    this.apply(locale);
    if (this.fromServerLocale(serverLocale)) {
      this.updateLocalUser(serverLocale);
    }
    return locale;
  }

  setLocale(locale: string, persistToServer = true): void {
    const normalized = this.normalizeUiLocale(locale);
    if (!normalized) {
      return;
    }
    this.apply(normalized);
    const backendLocale = this.toServerLocale(normalized);
    this.updateLocalUser(backendLocale);
    if (!persistToServer) {
      return;
    }
    if (BROWSER_STORAGE.getItem(AUTHENTICATED_STORAGE_KEY) === AUTHENTICATED_VALUE) {
      this.persistence.add(this.http.patch(this.api.myUiLocale, { uiLocale: backendLocale })
        .subscribe(() => {}, () => {}));
    }
  }

  ngOnDestroy(): void {
    this.persistence.unsubscribe();
  }

  getCurrentLocale(): UiLocale {
    return this.normalizeUiLocale(localStorage.getItem('lang')) || UiLocaleService.DEFAULT_LOCALE;
  }

  getContentLocale(): ContentLocale | null {
    const current = this.getCurrentLocale();
    if (current === CHINESE_UI_LOCALE) {
      return CHINESE_CONTENT_LOCALE;
    }
    return current === RAW_CONTENT_UI_LOCALE ? null : current as ContentLocale;
  }

  resolveContent(source?: string | null, translation?: ContentTranslation | null): string {
    const rawSource = source || EMPTY_CONTENT;
    const contentLocale = this.getContentLocale();
    if (!translation || !contentLocale) {
      return rawSource;
    }
    const translated = translation[contentLocale];
    return translated && translated.trim() ? translated : rawSource;
  }

  private apply(locale: UiLocale): void {
    BROWSER_STORAGE.setItem(UI_LOCALE_STORAGE_KEY, locale);
    PAGE_DOCUMENT.documentElement.lang = locale === CHINESE_UI_LOCALE ? CHINESE_CONTENT_LOCALE
      : locale === RAW_CONTENT_UI_LOCALE ? RAW_CONTENT_DOCUMENT_LANGUAGE : locale;
    this.translate.setDefaultLang(UiLocaleService.DEFAULT_LOCALE);
    this.translate.use(locale);
  }

  private normalizeUiLocale(locale?: string | null): UiLocale | null {
    if (!locale) {
      return null;
    }
    const normalized = locale.toLowerCase();
    return normalized === 'ru' || normalized === 'en' || normalized === 'ch' || normalized === 'bs'
      ? normalized as UiLocale
      : null;
  }

  private fromServerLocale(locale?: string | null): UiLocale | null {
    if (!locale) {
      return null;
    }
    const normalized = locale.toUpperCase();
    if (normalized === 'ZH') {
      return 'ch';
    }
    return this.normalizeUiLocale(normalized);
  }

  private toServerLocale(locale: UiLocale): string {
    return locale === 'ch' ? 'ZH' : locale.toUpperCase();
  }

  private fromLegacyLanguage(language?: string | null): UiLocale | null {
    if (!language) {
      return null;
    }
    const normalized = language.toUpperCase();
    if (normalized === 'RUSSIAN' || normalized === 'RU') {
      return 'ru';
    }
    if (normalized === 'CHINESE' || normalized === 'ZH' || normalized === 'CH') {
      return 'ch';
    }
    return normalized === 'ENGLISH' || normalized === 'EN' ? 'en' : null;
  }

  private getLocalUser(): User | null {
    try {
      return this.userService.getLocal();
    } catch (error) {
      return null;
    }
  }

  private updateLocalUser(serverLocale: string): void {
    const user = this.getLocalUser();
    if (!user) {
      return;
    }
    user.uiLocale = serverLocale;
    this.userService.setLocal(user);
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/services/ui-locale.service.ts